'use client'
import { Box, Flex, Group, Skeleton } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense, useMemo } from 'react'

import BasketCard from '@/app/[locale]/price/[slug]/_components/More/BasketCard'
import CoinCard from '@/app/[locale]/price/[slug]/_components/More/CoinCard'
import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import Swiper from '@/components/Swiper'
import { useCryptoBasket } from '@/services/invest'
import { CryptoBasket, CryptoBasketCoin, MoreCardType } from '@/types/invest'

type SwiperItem =
  | (CryptoBasketCoin & { type: MoreCardType.Coin })
  | (CryptoBasket & { type: MoreCardType.Basket })

const COIN_CARD_NUMBER = 2

function MoreFallback() {
  return (
    <Box mt={80} style={{ overflow: 'hidden' }}>
      {/* Title and controller area */}
      <Flex justify="space-between" align="center" mb={16}>
        <Skeleton
          h={32}
          w={160}
          style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
        />
      </Flex>

      {/* Content cards area */}
      <Box style={{ overflow: 'hidden' }}>
        <Group gap="md" wrap="nowrap">
          {[0, 1, 2, 3].map(index => (
            <Skeleton
              key={index}
              h={240}
              w={190}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: 16,
                flexShrink: 0,
              }}
            />
          ))}
        </Group>
      </Box>
    </Box>
  )
}

function MoreContent() {
  const { coin } = useCoinContext()
  const { data: basket } = useCryptoBasket(coin?.symbol)
  const t = useTranslations('more')

  const swiperList = useMemo(() => {
    // Get basket coins and call hooks directly
    const basketCoins = (basket?.crypto_baskets ?? []).reduce(
      (acc: { list: CryptoBasketCoin[]; seen: Set<string> }, b: CryptoBasket) => {
        ;(b.coins ?? []).forEach((c: CryptoBasketCoin) => {
          const key = `${c.name}__${c.symbol}`
          if (!acc.seen.has(key)) {
            acc.seen.add(key)
            if (c.name !== coin?.symbol && c.symbol.toLowerCase() !== coin?.symbol.toLowerCase()) {
              acc.list.push(c)
            }
          }
        })
        return acc
      },
      { list: [] as CryptoBasketCoin[], seen: new Set<string>() },
    ).list

    const filteredCoins = basketCoins.slice(0, COIN_CARD_NUMBER)

    const list: SwiperItem[] = []
    // Add coins with their original properties
    for (const item of filteredCoins) {
      list.push({
        type: MoreCardType.Coin as MoreCardType.Coin,
        ...item,
      })
    }
    // Add baskets
    for (const item of basket?.crypto_baskets || []) {
      list.push({
        type: MoreCardType.Basket as MoreCardType.Basket,
        ...item,
      })
    }

    return list
  }, [basket, coin?.symbol])

  if (swiperList.length === 0) {
    return null
  }

  return (
    <Box mt={80}>
      <Swiper
        title={t('title', { coinName: coin?.name })}
        swiperList={swiperList}
        renderSwiper={(item: SwiperItem, idx) =>
          item.type === MoreCardType.Basket ? (
            <BasketCard
              key={idx}
              {...(item as CryptoBasket & { type: MoreCardType.Basket })}
              priceChange={item.price_change}
            />
          ) : (
            <CoinCard key={idx} {...(item as CryptoBasketCoin & { type: MoreCardType.Coin })} />
          )
        }
      />
    </Box>
  )
}

function More() {
  return (
    <Suspense fallback={<MoreFallback />}>
      <MoreContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(More)
